'use client';

import { useState, useEffect, useRef } from 'react';
import HeroSection from './HeroSection';
import ProcessStep from './ProcessStep';
import VideoSection from './VideoSection';
import FAQSection from './FAQSection';
import DownloadableResources from './DownloadableResources';
import CTASection from './CTASection';
import Footer from './Footer';

interface ProcessStepData {
  number: number;
  title: string;
  description: string;
  icon: string;
  image: string;
  imageAlt: string;
  details: string[];
}

interface Video {
  id: number;
  title: string;
  duration: string;
  thumbnail: string;
  thumbnailAlt: string;
  description: string;
  category: string;
}

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
}

interface Resource {
  id: number;
  title: string;
  description: string;
  icon: string;
  fileSize: string;
  format: string;
}

export default function HowItWorksInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const stepsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleScrollToSteps = () => {
    if (stepsRef.current) {
      stepsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const processSteps: ProcessStepData[] = [
  {
    number: 1,
    title: "AI Analyzes Global Markets 24/7",
    description: "Our advanced artificial intelligence continuously monitors cryptocurrency and forex markets around the clock, processing millions of data points every second.",
    icon: "CpuChipIcon",
    image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74",
    imageAlt: "Digital dashboard showing real-time market data analytics with multiple charts and graphs on dark background",
    details: [
    "Processes over 100+ technical indicators simultaneously",
    "Analyzes market sentiment from news and social media",
    "Identifies patterns across multiple timeframes",
    "Monitors correlation between different asset classes"]

  },
  {
    number: 2,
    title: "Smart Signal Generation",
    description: "When our AI identifies high-probability trading opportunities, it generates precise signals with entry points, stop-loss, and take-profit levels.",
    icon: "BoltIcon",
    image: "https://images.unsplash.com/photo-1643109026104-91034fe17b87",
    imageAlt: "Mobile phone screen displaying trading signal notification with buy/sell indicators and price targets",
    details: [
    "87% average confidence rate on all signals",
    "Clear entry and exit points for every trade",
    "Risk-reward ratio calculated automatically",
    "Multiple signal types: scalping, day trading, swing trading"]

  },
  {
    number: 3,
    title: "Instant Notifications to Your Device",
    description: "Receive real-time push notifications the moment a new signal is generated, ensuring you never miss a profitable opportunity.",
    icon: "BellAlertIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bada16a0-1765906569288.png",
    imageAlt: "Smartphone with push notification alert showing new trading signal on lock screen",
    details: [
    "Push notifications within seconds of signal generation",
    "Customizable alert preferences and sound settings",
    "Signal details accessible even when app is closed",
    "Works on iOS and Android devices seamlessly"]

  },
  {
    number: 4,
    title: "Execute Trades with Confidence",
    description: "Follow the signal instructions to place your trade on your preferred exchange. Our detailed guidance makes execution simple and stress-free.",
    icon: "CheckBadgeIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4d34e75-1766894277169.png",
    imageAlt: "Trading platform interface showing successful trade execution with green confirmation checkmark",
    details: [
    "Step-by-step execution instructions included",
    "Compatible with all major crypto and forex exchanges",
    "Paper trading mode to practice risk-free",
    "Track your performance with built-in analytics"]

  }];


  const videos: Video[] = [
  {
    id: 1,
    title: "Getting Started with Signaly",
    duration: "5:32",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1b704a7e3-1766926911875.png",
    thumbnailAlt: "Video thumbnail showing Signaly app interface tutorial with play button overlay",
    description: "Learn how to set up your account, customize preferences, and receive your first trading signal in under 6 minutes.",
    category: "Getting Started"
  },
  {
    id: 2,
    title: "Understanding Signal Confidence Levels",
    duration: "8:15",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1eba39ad8-1767459528902.png",
    thumbnailAlt: "Educational video thumbnail displaying confidence level indicators and signal quality metrics",
    description: "Deep dive into how our AI calculates confidence levels and what different percentages mean for your trading strategy.",
    category: "Advanced"
  },
  {
    id: 3,
    title: "Risk Management Best Practices",
    duration: "6:45",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee1048c4-1765338815388.png",
    thumbnailAlt: "Risk management tutorial thumbnail showing portfolio allocation charts and stop-loss strategies",
    description: "Essential risk management techniques to protect your capital while maximizing returns with Signaly signals.",
    category: "Tips & Tricks"
  },
  {
    id: 4,
    title: "How to Read Market Sentiment",
    duration: "7:20",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0006a52-1764675341119.png",
    thumbnailAlt: "Market sentiment analysis video showing bullish and bearish indicators on trading charts",
    description: "Learn how Signaly's AI interprets market sentiment and how you can use this information to make better trading decisions.",
    category: "Advanced"
  },
  {
    id: 5,
    title: "Maximizing Profits with Take-Profit Levels",
    duration: "5:55",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1abdc6544-1767892348084.png",
    thumbnailAlt: "Profit optimization tutorial showing multiple take-profit targets on price chart",
    description: "Strategic approaches to setting and managing take-profit levels for optimal returns on every trade.",
    category: "Tips & Tricks"
  },
  {
    id: 6,
    title: "Paper Trading Tutorial",
    duration: "4:30",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a06fb494-1767892347293.png",
    thumbnailAlt: "Paper trading demo video showing simulated trading environment without real money risk",
    description: "Practice trading with virtual money to build confidence before risking real capital. Perfect for beginners.",
    category: "Getting Started"
  }];


  const faqs: FAQ[] = [
  {
    id: 1,
    question: "How does Signaly's AI generate trading signals?",
    answer: "Our AI uses advanced machine learning algorithms to analyze over 100 technical indicators, market sentiment data, news feeds, and historical patterns across multiple timeframes. It processes millions of data points every second to identify high-probability trading opportunities with an average 87% confidence rate.",
    category: "Getting Started"
  },
  {
    id: 2,
    question: "Do I need trading experience to use Signaly?",
    answer: "No trading experience is required! Signaly is designed for both beginners and experienced traders. Our signals come with clear instructions including entry points, stop-loss levels, and take-profit targets. We also provide educational resources and paper trading mode to help you learn risk-free.",
    category: "Getting Started"
  },
  {
    id: 3,
    question: "What is the confidence level and how should I interpret it?",
    answer: "The confidence level represents our AI's certainty about a signal's potential success based on historical data and current market conditions. Signals with 80%+ confidence have historically shown higher win rates. However, all trading involves risk, and past performance doesn't guarantee future results.",
    category: "Signals"
  },
  {
    id: 4,
    question: "How quickly do I receive signals after they're generated?",
    answer: "Signals are delivered to your device within seconds of generation through push notifications. You'll receive instant alerts even when the app is closed, ensuring you never miss a trading opportunity. The app also maintains a complete signal history for your reference.",
    category: "Signals"
  },
  {
    id: 5,
    question: "Can I use Signaly with any trading platform or exchange?",
    answer: "Yes! Signaly signals are platform-agnostic and work with all major cryptocurrency exchanges (Binance, Coinbase, Kraken, etc.) and forex brokers. You execute trades on your preferred platform while Signaly provides the analysis and signal generation.",
    category: "Technical"
  },
  {
    id: 6,
    question: "What's included in the free plan?",
    answer: "The free plan includes 5 signals per month, basic market analysis, and access to our educational resources. It's perfect for testing our service and understanding how our AI works before upgrading to a premium plan with unlimited signals.",
    category: "Pricing"
  },
  {
    id: 7,
    question: "How do I manage risk when following signals?",
    answer: "Every signal includes recommended stop-loss and take-profit levels calculated by our AI. We recommend never risking more than 1-2% of your total capital on a single trade. Our risk management guides and paper trading mode help you develop safe trading habits.",
    category: "Signals"
  },
  {
    id: 8,
    question: "Can I customize which types of signals I receive?",
    answer: "Absolutely! You can customize your preferences to receive signals for specific cryptocurrencies, forex pairs, timeframes (scalping, day trading, swing trading), and minimum confidence levels. This ensures you only get signals that match your trading style.",
    category: "Technical"
  },
  {
    id: 9,
    question: "What happens if I miss a signal notification?",
    answer: "All signals remain accessible in your app's signal history with complete details including the original entry point, current price action, and whether the signal is still valid. You can review past signals to learn from market movements and improve your trading decisions.",
    category: "Signals"
  },
  {
    id: 10,
    question: "Is there a money-back guarantee?",
    answer: "Yes! We offer a 30-day money-back guarantee on all premium plans. If you're not satisfied with Signaly's performance within the first month, contact our support team for a full refund—no questions asked.",
    category: "Pricing"
  },
  {
    id: 11,
    question: "How is Signaly different from other signal services?",
    answer: "Unlike manual signal services that rely on individual traders, Signaly uses AI that never sleeps, never experiences emotional bias, and processes far more data than any human could. Our 87% confidence rate and transparent performance tracking set us apart from competitors.",
    category: "Getting Started"
  },
  {
    id: 12,
    question: "What technical requirements do I need?",
    answer: "Signaly works on iOS 13+ and Android 8+ devices. You'll need a stable internet connection to receive real-time signals. The app is lightweight (under 50MB) and optimized for minimal battery usage while maintaining instant notification delivery.",
    category: "Technical"
  }];


  const resources: Resource[] = [
  {
    id: 1,
    title: "Beginner's Trading Guide",
    description: "Complete guide covering trading basics, terminology, and how to get started with Signaly signals.",
    icon: "BookOpenIcon",
    fileSize: "2.4 MB",
    format: "PDF"
  },
  {
    id: 2,
    title: "Risk Management Checklist",
    description: "Essential checklist to protect your capital and manage risk effectively on every trade.",
    icon: "ClipboardDocumentCheckIcon",
    fileSize: "850 KB",
    format: "PDF"
  },
  {
    id: 3,
    title: "Signal Execution Worksheet",
    description: "Track your trades, analyze performance, and improve your execution strategy over time.",
    icon: "DocumentChartBarIcon",
    fileSize: "1.2 MB",
    format: "Excel"
  },
  {
    id: 4,
    title: "Market Analysis Templates",
    description: "Professional templates to conduct your own market analysis alongside Signaly's AI insights.",
    icon: "ChartBarIcon",
    fileSize: "3.1 MB",
    format: "PDF"
  },
  {
    id: 5,
    title: "Trading Psychology Guide",
    description: "Master the mental game of trading with strategies to control emotions and maintain discipline.",
    icon: "LightBulbIcon",
    fileSize: "1.8 MB",
    format: "PDF"
  },
  {
    id: 6,
    title: "Crypto Trading Glossary",
    description: "Comprehensive glossary of cryptocurrency and trading terms every trader should know.",
    icon: "AcademicCapIcon",
    fileSize: "950 KB",
    format: "PDF"
  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-16" />
        <div className="animate-pulse space-y-8 p-8">
          <div className="h-64 bg-muted rounded-lg" />
          <div className="h-96 bg-muted rounded-lg" />
          <div className="h-96 bg-muted rounded-lg" />
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <HeroSection onScrollToSteps={handleScrollToSteps} />
      
      <div ref={stepsRef} className="py-20 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              The Signaly Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From AI analysis to profitable trades—here&apos;s exactly how Signaly works in four simple steps.
            </p>
          </div>
          
          <div className="space-y-24">
            {processSteps.map((step, index) =>
            <ProcessStep
              key={step.number}
              step={step}
              isReversed={index % 2 !== 0} />

            )}
          </div>
        </div>
      </div>

      <VideoSection videos={videos} />
      <DownloadableResources resources={resources} />
      <FAQSection faqs={faqs} />
      <CTASection />
      <Footer />
    </div>);

}